/**
 * Helpers for resolving the user's currently active organisation.
 *
 * The active organisation is identified by its integer `id`, but `user.organisationId`
 * can arrive as either a number (login / org switcher) or a string (e.g. the email
 * payment-link flow decodes it from a token). Matching must therefore be
 * type-insensitive, otherwise the active organisation cannot be found and both the
 * header reference and the advanced-features allowlist silently fall back to empty.
 */

const idsMatch = (a, b) => a != null && b != null && String(a) === String(b);

/**
 * Sentinel `organisationId` used to represent the "All Organisations" selection
 * in the header switcher. When active, the payments page aggregates payments
 * across every organisation the user belongs to, and organisation-scoped admin
 * pages (user/organisation management) are hidden.
 */
export const ALL_ORGANISATIONS_ID = 'all-organisations';

/** Display name shown for the "All Organisations" selection. */
export const ALL_ORGANISATIONS_NAME = 'All Organisations';

/**
 * Whether the user currently has the aggregate "All Organisations" view selected.
 * @param {{ organisationId?: string|number|null }} user
 * @returns {boolean}
 */
export const isAllOrganisationsSelected = (user) =>
    String(user?.organisationId) === ALL_ORGANISATIONS_ID;

/**
 * Finds the active organisation record for the given user.
 * @param {{ organisationId?: string|number|null, organisations?: Array<{ id: string|number }> }} user
 * @returns {object|null}
 */
export const getActiveOrganisation = (user) =>
    user?.organisations?.find((org) => idsMatch(org.id, user.organisationId)) || null;

/**
 * Resolves the active organisation's UUID (the `organisationId` field on the record,
 * shown next to the header switcher and used for the feature allowlist).
 * @param {object} user
 * @returns {string}
 */
export const getActiveOrganisationUuid = (user) =>
    getActiveOrganisation(user)?.organisationId || '';

export { idsMatch as organisationIdsMatch };
